import DataGrid from '@components/DataGrid';
import { Box, Button, Typography } from '@mui/material';
import { AccessRequest } from '@synergetx/puppet-interview-types';
import {
    ACCESS_REQUESTS_COLUMNS,
    ACCESS_REQUESTS_COLUMN_VISIBILITY,
    ACCESS_REQUESTS_EXPORT_COLUMNS,
    ACCESS_REQUESTS_INITIAL_SORT,
} from '@constants/accessRequestsColumns';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AccessReviewDialog from '@components/AccessReviewDialog';
import AccessRequestTabs from '@components/AccessRequestTabs';
import { getAccessRequests } from '@modules/firebase/access-requests';
import { logError } from '@modules/logging';
import { exportToCsv } from '@utils/exportCsv';
import { format } from 'date-fns/format';
import DownloadIcon from '@mui/icons-material/Download';

const URL_SELECTION_QUERY_KEY = 'id';

function AccessRequestsScreen() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [manuallySelected, setManuallySelected] = useState<AccessRequest>();
    const [allRequests, setAllRequests] = useState<AccessRequest[] | null>(null);
    const [requestsToShow, setRequestsToShow] = useState<AccessRequest[] | null>(null);

    const selectedQueryId = searchParams.get(URL_SELECTION_QUERY_KEY);

    useEffect(() => {
        getAccessRequests()
            .then((requests) => setAllRequests(requests))
            .catch((error) => {
                console.error('AccessRequestsScreen: useEffect: getAccessRequests: error', error);
                logError(error);
            });
    }, []);

    // The dialog target is derived rather than synced through an effect: an explicit
    // row click wins, otherwise fall back to the `?id=` deep link once requests load.
    const requestFromQuery = useMemo(() => {
        if (!selectedQueryId || !allRequests?.length) return undefined;
        return allRequests.find((request) => request.id === selectedQueryId);
    }, [allRequests, selectedQueryId]);

    const requestToEdit = manuallySelected ?? requestFromQuery;

    const onCloseDialog = useCallback(() => {
        setManuallySelected(undefined);
        if (searchParams.has(URL_SELECTION_QUERY_KEY)) {
            const next = new URLSearchParams(searchParams);
            next.delete(URL_SELECTION_QUERY_KEY);
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleExportCsv = useCallback(() => {
        if (!allRequests?.length) return;

        exportToCsv(
            ACCESS_REQUESTS_COLUMNS,
            allRequests,
            `Access Requests ${new Date().toLocaleString()}`,
            {
                includeColumns: ACCESS_REQUESTS_EXPORT_COLUMNS,
                formatters: {
                    createdAt: (value: unknown) => (value ? format(value as number, 'Pp') : ''),
                    updatedAt: (value: unknown) => (value ? format(value as number, 'Pp') : ''),
                },
            },
        );
    }, [allRequests]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0, // Allow flex shrinking
            }}
        >
            <DataGrid
                columns={ACCESS_REQUESTS_COLUMNS}
                rows={requestsToShow ?? []}
                loading={requestsToShow === null}
                getRowId={(row) => row.id}
                columnVisibilityModel={ACCESS_REQUESTS_COLUMN_VISIBILITY}
                initialSortModel={ACCESS_REQUESTS_INITIAL_SORT}
                onRowClick={(row) => setManuallySelected(row)}
                toolbar={
                    <>
                        <Typography variant="h5" sx={{ mr: 2 }}>
                            Users Requesting Access
                        </Typography>
                        <AccessRequestTabs
                            allRequests={allRequests}
                            setRequestsToShow={setRequestsToShow}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportCsv}
                            disabled={!allRequests?.length}
                            sx={{ ml: 2 }}
                        >
                            Export CSV
                        </Button>
                    </>
                }
            />
            {requestToEdit ? (
                <AccessReviewDialog
                    onClose={onCloseDialog}
                    requestToEdit={requestToEdit}
                    setAllRequests={setAllRequests}
                />
            ) : null}
        </Box>
    );
}

export default memo(AccessRequestsScreen);
